import React, { useEffect, useRef, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { getLocale, useIntl, history } from 'umi';
import moment from 'moment';
import classNames from 'classnames';
import RTCModel from '@/models/rtc';
import MeetingModel from '@/models/meeting';
import UserModel from '@/models/user';
import Style from './index.less';
import {
  Button,
  MeetingId,
  YunxinBottom,
  ErrorBrowserHeader,
  Icon,
  Modal,
  message,
} from '@/components';
import DevicePanel from '../component/DevicePanel';
import PrivatePolicy, { openPrivatePolicy } from '../component/PrivatePolicy';
import FirstEnter from '../component/FirstEnter';
import { clearSessionStorage, getQueryString, stringTimeToTimeNumber } from '@/utils/common';
import FeedbackPanelH5Drawer, { H5DraserRef } from '@/components/FeedbackPanel/H5Drawer';
import { user } from '@/models';
import { LOGIN_TYPE } from '../component/LoginHeader';
import { TIPS_TYPE } from '@/types/tips';
import PwdModal from '../component/PwdModal';
import { setLocalPassword } from '@/utils/localStorage';
import { elkReport } from '@/utils/elkReport';

interface ReadyH5Props {
  RTCInstance?: RTCModel;
  meeting?: MeetingModel;
  user?: UserModel;
  meetingId: string;
  mustCheck: boolean;
  checkMIC: () => Promise<void>;
  onMicSwitchChange: (value: boolean) => Promise<void>;
  isGuest: boolean;
}

const ReadyH5: React.FC<ReadyH5Props> = inject(({ store }) => {
  return {
    RTCInstance: store.rtc,
    meeting: store.meeting,
    user: store.user,
  };
})(
  observer((props) => {
    const { meeting, meetingId, mustCheck, checkMIC, onMicSwitchChange, isGuest } = props;
    const { formatMessage } = useIntl();
    const [isFocus, setIsFocus] = useState<boolean>(false);
    const inputRef = useRef<any>(null);
    const readyRef = useRef<HTMLDivElement>(null);
    const [agreePolicy, setAgreePolicy] = useState(false);
    const [height, setHeight] = useState(0);
    const [errBrowserShow, setErrBrowserShow] = useState(true);
    const feedbackDef = useRef<H5DraserRef>(null);
    const [password, setPassword] = useState('');
    const [hasError, setHasError] = useState<boolean>(false);
    const [inputValue, setInputValue] = useState('');
    const [btnLoading, setBtnLoading] = React.useState<boolean>(false);
    const [currentAvatarId] = React.useState<number>(0);
    const [firstMemberNotifyOn, setFirstMemberNotifyOn] = useState(false);
    const [pwdModalVisible, setPwdModalVisible] = useState<boolean>(false);

    const onInputChange = React.useCallback((e: any) => {
      // eslint-disable-next-line no-misleading-character-class
      const emojiReg =
        /[\uD83C|\uD83D|\uD83E][\uDC00-\uDFFF][\u200D|\uFE0F]|[\uD83C|\uD83D|\uD83E][\uDC00-\uDFFF]|[0-9|*|#]\uFE0F\u20E3|[0-9|#]\u20E3|[\u203C-\u3299]\uFE0F\u200D|[\u203C-\u3299]\uFE0F|[\u2122-\u2B55]|\u303D|[\A9|\AE]\u3030|\uA9|\uAE|\u3030/gi;
      let { value } = e.target;
      if (emojiReg.test(value)) {
        value = value.replace(emojiReg, '');
      }
      setInputValue(value);
      if (value) {
        setHasError(false);
      }
    }, []);

    const joinMeeting = React.useCallback(
      async (name: string, password?: string) => {
        if (!user?.isOnline) {
          message.info({
            content: formatMessage({ id: 'OFFLINE' }),
            noIcon: true,
          });
          return;
        }

        try {
          // setBtnLoading(true);
          let authType;
          if (user?.user?.openAuthed && user?.user?.authType === LOGIN_TYPE.NEOpenID) {
            authType = LOGIN_TYPE.NEOpenID;
          } else if (user?.user?.openAuthed && user?.user?.authType === LOGIN_TYPE.Oscar) {
            authType = LOGIN_TYPE.Oscar;
          } else {
            authType = LOGIN_TYPE.GUEST;
          }
          if (window.isMobile) {
            authType = LOGIN_TYPE.GUEST;
          }

          const resp = await user?.login(
            authType,
            name,
            meetingId,
            currentAvatarId,
            getQueryString('pwd')
          );
          window.sessionStorage.setItem('waterMark', JSON.stringify(resp?.data?.watermark));
          await meeting?.getInfo(
            {
              meetingId,
              scheduleSt: (history.location.query?.scheduleSt as string) || '',
              firstMemberNotifyOn: firstMemberNotifyOn ? '1' : '0',
              password,
            },
            true
          );
          password && setLocalPassword(meetingId, password);
          const search = new URLSearchParams(window.location.search);
          const nextSearch = new URLSearchParams();
          for (const [key, value] of search.entries()) {
            if (['isRecruitMeeting', 'source'].includes(key)) {
              nextSearch.append(key, value);
            }
          }
          console.log('nextSearch', nextSearch);
          history.replace({
            pathname: `${meetingId}/meeting`,
            search: nextSearch.toString(),
          });
          // setPwdModalVisible(true);
        } catch (err: any) {
          console.warn(err);
          clearSessionStorage();
          elkReport({ type: '登录失败', logData: err });
          if ([404, 510001].includes(err.status)) {
            history.push({
              pathname: `/meet/${meetingId}/tips`,
              query: {
                type: TIPS_TYPE.NOT_FOUND,
              },
            });
          }
          // 5101 会议已失效 | 5102 未开启 web 入会开关
          if ([570004, 511003].includes(err.status)) {
            history.push({
              pathname: `/meet/${meetingId}/forbid`,
              query: {
                errorCode: err.status,
              },
            });
          }

          // 5101 会议已失效 | 5102 未开启 web 入会开关
          if ([5101, 5102, 570001, 510002].includes(err.status)) {
            history.push({
              pathname: `/meet/${meetingId}/tips`,
              query: {
                type: TIPS_TYPE.FINISHED,
              },
            });
          }

          if ([510006].includes(err.status)) {
            message.info({
              content: formatMessage({ id: 'meetingNeedPassword' }),
              noIcon: true,
            });
            setPwdModalVisible(true);
          }

          if ([510007].includes(err.status)) {
            message.info({
              content: formatMessage({ id: 'errorPassword' }),
              noIcon: true,
            });
          }
          setBtnLoading(false);
        }
      },
      [meetingId, user, meeting, currentAvatarId, formatMessage, firstMemberNotifyOn, isGuest]
    );

    const checkPrivate = React.useCallback(
      async (callback: (name: string, password: string) => void, agree: boolean) => {
        const linkStyle: React.CSSProperties = {
          cursor: 'pointer',
          color: '#1f86ff',
          marginLeft: getLocale() === 'zh-CN' ? '0.5em' : 0,
          marginRight: getLocale() === 'ja' ? '0.5em' : 0,
        };
        // 先判断是否有入会人员 如果没有提示错误
        if (!inputValue) {
          setHasError(true);
          return;
        }
        console.log(getLocale(), getLocale() === 'zh-CN' ? '0.5em' : 0, linkStyle);
        if (agree) {
          callback(inputValue, password);
        } else {
          Modal.confirm({
            title: formatMessage({ id: 'PROMPT' }).replace(/^[a-z]/, (value) =>
              value.toLocaleUpperCase()
            ),
            okText: formatMessage({ id: 'AGREE' }),
            cancelText: formatMessage({ id: 'CANCEL' }),
            okButtonProps: {
              danger: false,
            },
            content:
              getLocale() === 'ja' ? (
                <div>
                  <span style={linkStyle} onClick={openPrivatePolicy}>
                    {formatMessage({ id: 'PRIVACY_POLICY' })}
                  </span>
                  <span>{formatMessage({ id: 'PLEASE_AGREE_TO' })}</span>
                </div>
              ) : (
                <div>
                  <span>{formatMessage({ id: 'PLEASE_AGREE_TO' })}</span>
                  <span style={linkStyle} onClick={openPrivatePolicy}>
                    {formatMessage({ id: 'PRIVACY_POLICY' })}
                  </span>
                </div>
              ),
            onOk: () => {
              setAgreePolicy(true);
              callback(inputValue, password);
            },
          });
        }
      },
      [inputValue, password]
    );

    useEffect(() => {
      if (!window.location.href.includes('guest=1')) {
        setInputValue(user?.name || '');
      }
    }, [user?.name]);

    useEffect(() => {
      const cb = () => {
        setHeight(0);
        setTimeout(() => {
          readyRef.current && setHeight(readyRef.current.offsetHeight);
        }, 100);
      };
      setTimeout(() => {
        readyRef.current && setHeight(readyRef.current.offsetHeight);
      }, 100);
      window.addEventListener('orientationchange', cb);
      return () => {
        window.removeEventListener('orientationchange', cb);
      };
    }, []);

    const meetCheckinPassword = async (password: string) => {
      if (!user?.isOnline) {
        message.info({
          content: formatMessage({ id: 'OFFLINE' }),
          noIcon: true,
        });
        return;
      }

      try {
        await meeting?.getInfo(
          {
            meetingId,
            scheduleSt: (history.location.query?.scheduleSt as string) || '',
            firstMemberNotifyOn: firstMemberNotifyOn ? '1' : '0',
            password,
          },
          true
        );
        password && setLocalPassword(meetingId, password);
        setPwdModalVisible(false);
        const search = new URLSearchParams(window.location.search);
        const nextSearch = new URLSearchParams();
        for (const [key, value] of search.entries()) {
          if (['isRecruitMeeting', 'source'].includes(key)) {
            nextSearch.append(key, value);
          }
        }

        console.log('nextSearch', nextSearch);
        history.replace({
          pathname: `${meetingId}/meeting`,
          search: nextSearch.toString(),
        });
      } catch (err: any) {
        console.warn(err);
        clearSessionStorage();
        elkReport({ type: '登录失败', logData: err });
        if ([404, 510001].includes(err.status)) {
          history.push({
            pathname: `/meet/${meetingId}/tips`,
            query: {
              type: TIPS_TYPE.NOT_FOUND,
            },
          });
        }
        // 5101 会议已失效 | 5102 未开启 web 入会开关
        if ([570004, 511003].includes(err.status)) {
          history.push({
            pathname: `/meet/${meetingId}/forbid`,
            query: {
              errorCode: err.status,
            },
          });
        }

        // 5101 会议已失效 | 5102 未开启 web 入会开关
        if ([5101, 5102, 570001, 510002].includes(err.status)) {
          history.push({
            pathname: `/meet/${meetingId}/tips`,
            query: {
              type: TIPS_TYPE.FINISHED,
            },
          });
        }

        if ([510006].includes(err.status)) {
          message.info({
            content: formatMessage({ id: 'meetingNeedPassword' }),
            noIcon: true,
          });
          setPwdModalVisible(true);
        }

        if ([510007].includes(err.status)) {
          message.info({
            content: formatMessage({ id: 'errorPassword' }),
            noIcon: true,
          });
        }
      }
    };
    const handleSubmit = (password: string) => {
      meetCheckinPassword(password);
    };

    return (
      <>
        <PwdModal
          visible={pwdModalVisible}
          onClose={() => {
            setPwdModalVisible(false);
          }}
          onOk={handleSubmit}
        ></PwdModal>
        <div className={Style.ready} ref={readyRef} style={height ? { height } : {}}>
          <FeedbackPanelH5Drawer ref={feedbackDef} type="meeting" />
          <div className={Style['feedback-btn']} onClick={() => feedbackDef.current?.open()}>
            <Icon name="icon-feedback" size={15} className={Style.feedbackicon} />
            {/* 反馈 */}
            {formatMessage({ id: 'FEEDBACK' })}
          </div>
          <div className={Style.content}>
            <div className={Style.box}>
              <div className={Style.top}>
                <ErrorBrowserHeader
                  show={errBrowserShow}
                  onClose={() => setErrBrowserShow(false)}
                />

                <div className={Style.header}>
                  <div className={Style.title}>{meeting?.info?.meetingName}</div>
                </div>
                <div className={Style.desc}>
                  <div className={Style['desc-item']}>
                    <div className={Style['desc-name']}>ID：</div>
                    <div className={Style['desc-info']}>
                      <MeetingId meetingId={meetingId} />
                    </div>
                  </div>
                  {meeting?.isCalendarMode ? (
                    <>
                      <div className={Style.line} />
                      <div className={Style['desc-item']}>
                        <div className={Style['desc-info']}>
                          {moment(stringTimeToTimeNumber(meeting?.info?.scheduleStartTime)).format(
                            'YYYY/MM/DD HH:mm'
                          )}
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
              <div className={Style.center}>
                <div className={Style['center-content']}>
                  <div
                    className={classNames(
                      Style.name,
                      { [Style.actived]: isFocus },
                      { [Style.error]: hasError }
                    )}
                    onClick={() => inputRef.current?.focus()}
                  >
                    <input
                      ref={inputRef}
                      className={Style['name-input']}
                      type="text"
                      maxLength={20}
                      value={inputValue}
                      onChange={onInputChange}
                      onFocus={() => {
                        setIsFocus(true);
                      }}
                      onBlur={() => {
                        setIsFocus(false);
                      }}
                      placeholder={formatMessage({ id: 'ENTER_NAME' })}
                    />
                    <span className={classNames(Style.errTip, { [Style.show]: hasError })}>
                      {formatMessage({ id: 'ENTER_YOUR_NAME' })}
                    </span>
                  </div>

                  <DevicePanel
                    mustCheck={mustCheck}
                    checkMIC={checkMIC}
                    onMicSwitchChange={onMicSwitchChange}
                  />
                  <div style={{ margin: '28px auto 0' }}>
                    {meeting?.meetingStatus !== 2 && (
                      <FirstEnter onChange={setFirstMemberNotifyOn} />
                    )}
                    <PrivatePolicy agreePolicy={agreePolicy} setAgreePolicy={setAgreePolicy} />
                  </div>
                  <Button
                    onClick={() => {
                      checkPrivate(joinMeeting, agreePolicy);
                    }}
                    // disabled={hasError}
                    loading={btnLoading}
                    type="primary"
                    className={classNames(Style['join-btn'], { [Style.disabled]: !inputValue })}
                  >
                    {formatMessage({ id: 'JOIN' })}
                  </Button>
                </div>
              </div>
            </div>
            <YunxinBottom />
          </div>
        </div>
      </>
    );
  })
);

export default ReadyH5;
